import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  createFavoritesSlice,
  createPageHistorySlice,
  createPagesSlice,
  createRecentMoveTargetsSlice,
  createRecentPagesSlice,
  createSidebarSlice,
} from './actions';
import { BrainSpaceGroupKey, BrainStore } from './types';
import { createIndexedDBStorage } from '../utils/indexedDBAdapter';

export const BRAIN_PERSIST_KEY = 'brain-selection';
const BRAIN_PERSIST_VERSION = 2;

type BrainPersistedState = Pick<
  BrainStore,
  'selectedPageId' | 'recentMoveTargets' | 'expandedGroupTypes'
>;

export const useBrainStore = create<BrainStore>()(
  persist(
    (set, get, store) => ({
      ...createSidebarSlice(set, get, store),
      ...createPagesSlice(set, get, store),
      ...createFavoritesSlice(set, get, store),
      ...createRecentPagesSlice(set, get, store),
      ...createPageHistorySlice(set, get, store),
      ...createRecentMoveTargetsSlice(set, get, store),
      _hasHydrated: false,
      setHasHydrated: (value) => set({ _hasHydrated: value }),
    }),
    {
      name: BRAIN_PERSIST_KEY,
      version: BRAIN_PERSIST_VERSION,
      partialize: (state) => ({
        selectedPageId: state.selectedPageId,
        recentMoveTargets: state.recentMoveTargets,
        expandedGroupTypes: state.expandedGroupTypes,
      }),
      storage: {
        getItem: async (name: string) => {
          const indexedDBStorage = createIndexedDBStorage();
          const str = await indexedDBStorage.getItem(name);
          if (!str) return null;
          const data = JSON.parse(str);

          if (Array.isArray(data.state?.expandedGroupTypes)) {
            data.state.expandedGroupTypes = new Set(
              data.state.expandedGroupTypes,
            );
          }

          return data;
        },
        setItem: async (name: string, value: unknown) => {
          const indexedDBStorage = createIndexedDBStorage();
          const typedValue = value as {
            state?: { expandedGroupTypes?: Set<BrainSpaceGroupKey> };
          };
          const valueToStore = {
            ...typedValue,
            state: {
              ...typedValue.state,
              expandedGroupTypes: Array.from(
                typedValue.state?.expandedGroupTypes ?? [],
              ),
            },
          };
          await indexedDBStorage.setItem(name, JSON.stringify(valueToStore));
        },
        removeItem: async (name: string) => {
          const indexedDBStorage = createIndexedDBStorage();
          await indexedDBStorage.removeItem(name);
        },
      },
      migrate: (persistedState, version) => {
        if (version < BRAIN_PERSIST_VERSION) {
          const previous = persistedState as {
            selectedPageId?: string | null;
            recentMoveTargets?: BrainStore['recentMoveTargets'];
          };
          return {
            selectedPageId: previous?.selectedPageId ?? null,
            recentMoveTargets: previous?.recentMoveTargets ?? [],
            expandedGroupTypes: new Set<BrainSpaceGroupKey>(),
          };
        }
        return persistedState as BrainPersistedState;
      },
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Brain store hydration failed:', error);
        }
        if (state) {
          state.setHasHydrated(true);
        } else {
          useBrainStore.getState().setHasHydrated(true);
        }
      },
    },
  ),
);
